/**
 * Action bar for the lesson video screen — mirrors the feed ActionBar
 * (client/src/feed/components/ui/ActionBar.tsx) minus the like button, since
 * iOS renders the shared VideoScreenView with showLikeButton={false} in lessons
 * (client-native/src/features/lesson/activities/VideoNew.tsx:437-438). The
 * wordlist button is flag-gated by the caller (`wordlist_in_feed`).
 */
import { observer } from "@legendapp/state/react";
import { MessageSquareText, LayoutList } from "lucide-react";
import { captionsVisible$ } from "@/feed/state/videoState";
import { playbackRate$, cycleSpeed } from "@/feed/state/userState";

interface LessonActionBarProps {
  showWordlistButton: boolean;
  onWordlistPressed: () => void;
  showCaptionsToggle: boolean;
}

// iOS lesson rail order (no like button): speed → wordlist → captions. Play/pause
// is the centre control (CenterPlayPause), not a rail button.
const LessonActionBar = observer(function LessonActionBar({
  showWordlistButton,
  onWordlistPressed,
  showCaptionsToggle,
}: LessonActionBarProps) {
  const rate = playbackRate$.get();
  const captionsOn = captionsVisible$.get();

  return (
    <div
      className="flex flex-col items-center gap-0"
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={cycleSpeed}
        aria-label="Playback speed"
        className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-150 active:scale-110"
      >
        <span className="text-xl font-semibold text-white drop-shadow-md">
          {rate}x
        </span>
      </button>

      {showWordlistButton && (
        <button
          type="button"
          onClick={onWordlistPressed}
          aria-label="Mark words you know"
          className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-150 active:scale-110"
        >
          <LayoutList className="w-7 h-7 text-white drop-shadow-md" />
        </button>
      )}

      {showCaptionsToggle && (
        <button
          type="button"
          onClick={() => captionsVisible$.set((v) => !v)}
          aria-label={captionsOn ? "Hide captions" : "Show captions"}
          className="w-12 h-12 rounded-full flex items-center justify-center transition-transform duration-150 active:scale-110"
        >
          <MessageSquareText
            className={`w-7 h-7 drop-shadow-md ${captionsOn ? "text-white" : "text-white/50"}`}
          />
        </button>
      )}
    </div>
  );
});

export default LessonActionBar;
